import type { DishResponse, Menu } from "api-types";

import {
  type GuestMenuSection,
  type MenuTreeNode,
  buildGuestMenuTree,
  isValidGuestGroupHeader,
} from "./menu-tree";
import { allergenBands } from "./staff-allergen-bands";

/**
 * The staff view of a menu section's structure.
 *
 * One rule decides the whole shape: a choice group never puts its options in
 * the menu list. A group under a dish folds into that dish's drawer. A group at
 * the top of a section has no dish to fold into, so it takes a row of its own
 * and opens its options in the drawer.
 *
 * Listing them instead reads as a menu of eleven things when the restaurant
 * offers two: Frankie's Wings alone spends eight rows on three sizes and five
 * flavours, and the dish a waiter is scanning for is below all of it.
 *
 * Structure we cannot describe keeps its row. The guest menu hides that, which
 * is right for a guest and wrong for staff: a waiter who is not told answers
 * from the rest of the screen.
 *
 * A dish reference that does not resolve is a different thing and stays quiet.
 * The menus service filters `dishesInDisplayOrder` for hidden and unverified
 * dishes while passing `itemsInDisplayOrder` through untouched, so a dangling
 * reference is the ordinary shape of a restaurant hiding a dish. The flat list
 * showed nothing for those, and so does this.
 */

/** The editor's cap. Deeper sections render flattened rather than off-screen. */
export const MAX_STAFF_DEPTH = 3;

/** Theme spacing units one level of nesting steps in by, on every surface. */
export const INDENT_STEP = 2;

export type StaffDishRow = {
  kind: "dish";
  id: string;
  depth: number;
  node: MenuTreeNode;
  dish: DishResponse;
  /** Direct child groups: the decisions this dish asks for. */
  choiceCount: number;
  /** Anything under this dish we could not read, at any depth. */
  unreadableCount: number;
};

export type StaffGroupRow = {
  kind: "group";
  id: string;
  depth: number;
  node: MenuTreeNode;
  /** Null when the header record is unusable. The row says so rather than going. */
  title: string | null;
  /** The restaurant's own words for the choice, empty when it wrote none. */
  description: string;
  required: boolean;
  optionCount: number;
  /** Groups nested directly under this one. See `countChoiceGroups`. */
  groupCount: number;
};

export type StaffListRow = StaffDishRow | StaffGroupRow;

export type StaffSectionRows =
  | { kind: "legacy" }
  | { kind: "rows"; rows: StaffListRow[] };

/** Wraps a dish with no structural row of its own, so every surface takes a node. */
export const staffDishNode = (dish: DishResponse): MenuTreeNode => ({
  id: dish.id,
  sourceId: dish.id,
  kind: "dish",
  depth: 0,
  dish,
  children: [],
});

export const dishChoiceGroups = (node: MenuTreeNode): MenuTreeNode[] =>
  node.children.filter((child) => child.kind === "header");

/**
 * What a group actually offers: the dishes a guest picks between. A nested
 * group is a further step to work through rather than something to choose, and
 * a withheld dish cannot be served, so neither is a number staff can act on.
 */
export const countOptions = (node: MenuTreeNode): number =>
  node.children.filter((child) => child.kind === "dish" && !!child.dish).length;

/**
 * The groups nested directly under this one.
 *
 * Counted separately because a group can hold only other groups, and reporting
 * that as "0 options" reads as a broken or empty choice when it is neither: it
 * asks for two picks rather than offering none. Summing the dishes underneath
 * would be worse still, since "7 options" implies one pick from seven when the
 * guest makes one from each group.
 */
export const countChoiceGroups = (node: MenuTreeNode): number =>
  node.children.filter(
    // Readable ones only, the same way `countOptions` skips a withheld dish.
    // A broken header renders as "can't be read", so counting it would promise
    // a pick that never appears.
    (child) => child.kind === "header" && isValidGuestGroupHeader(child.header)
  ).length;

/**
 * The rows a section contributes to the menu list. `legacy` covers both a
 * section with no structural order and one whose order did not parse: staff
 * fall back to the flat dish list either way, since a section that renders
 * nothing is worse than a section that renders without its groups.
 */
export const buildStaffMenuRows = (
  section: GuestMenuSection
): StaffSectionRows => {
  const tree = buildGuestMenuTree(section);
  if (tree.kind !== "tree") return { kind: "legacy" };

  const rows: StaffListRow[] = [];

  const walk = (nodes: MenuTreeNode[], depth: number) => {
    for (const node of nodes) {
      const rowDepth = Math.min(depth, MAX_STAFF_DEPTH);

      if (node.kind === "dish") {
        // Hidden or unverified, and withheld by the response on purpose.
        if (!node.dish) continue;
        rows.push({
          kind: "dish",
          id: node.id,
          depth: rowDepth,
          node,
          dish: node.dish,
          choiceCount: dishChoiceGroups(node).length,
          // Taken from the band maths rather than counted again here, so the
          // row and the card it opens cannot disagree about what is missing.
          unreadableCount: allergenBands(node).unreadable,
        });
        // The groups this dish owns fold into its drawer. A dish nested under a
        // dish does not: the editor can author one, but nothing states how the
        // two are ordered together, so the bands score it unreadable and it
        // keeps a row of its own rather than disappearing off the menu.
        walk(
          node.children.filter((child) => child.kind === "dish"),
          depth + 1
        );
        continue;
      }

      const header = isValidGuestGroupHeader(node.header) ? node.header : null;
      rows.push({
        kind: "group",
        id: node.id,
        depth: rowDepth,
        node,
        title: header ? header.title : null,
        description: header?.description ?? "",
        required: header ? header.required : false,
        optionCount: countOptions(node),
        groupCount: countChoiceGroups(node),
      });
      // No recursion: the options open in the drawer, like every other group.
    }
  };

  walk(tree.roots, 0);
  return { kind: "rows", rows };
};

export type StaffSearchResult = {
  id: string;
  node: MenuTreeNode;
  dish: DishResponse;
  /** Nearest ancestor first, section last. */
  trail: string[];
};

const matchesFilter = (dish: DishResponse, needle: string): boolean =>
  dish.name.toLowerCase().includes(needle) ||
  !!dish.description?.toLowerCase().includes(needle);

/**
 * Search reaches the options folded out of the list, which is the point: a
 * waiter asked about a sauce searches its name. A bare result named only
 * "Garlic aioli" would be the bug this module exists to fix, so every result
 * carries the trail that says what it belongs to.
 */
export const searchStaffMenu = (
  menu: Menu | undefined,
  filter: string
): StaffSearchResult[] => {
  const needle = filter.trim().toLowerCase();
  if (!needle) return [];

  const results: StaffSearchResult[] = [];

  for (const section of menu?.menuSectionsInDisplayOrder ?? []) {
    const sectionName = section.menuSectionName;
    const tree = buildGuestMenuTree(section);

    if (tree.kind !== "tree") {
      for (const dish of section.dishesInDisplayOrder ?? []) {
        if (!matchesFilter(dish, needle)) continue;
        results.push({
          // Section-scoped like a tree node's id: results are one flat list, and
          // the same dish can sit in two sections.
          id: `${section.id}:${dish.id}`,
          node: staffDishNode(dish),
          dish,
          trail: [sectionName],
        });
      }
      continue;
    }

    const walk = (nodes: MenuTreeNode[], ancestors: string[]) => {
      for (const node of nodes) {
        if (
          node.kind === "dish" &&
          node.dish &&
          matchesFilter(node.dish, needle)
        ) {
          results.push({
            id: node.id,
            node,
            dish: node.dish,
            trail: [...ancestors, sectionName],
          });
        }
        const header = isValidGuestGroupHeader(node.header)
          ? node.header
          : null;
        const label = node.kind === "dish" ? node.dish?.name : header?.title;
        walk(node.children, label ? [label, ...ancestors] : ancestors);
      }
    };

    walk(tree.roots, []);
  }

  return results;
};
